import { useRef, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Upload, X, CheckCircle2, Sparkles, ImagePlus, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const MIN_DESC = 20;
const MAX_DESC = 1000;

interface PhotoSlot {
  file: File | null;
  preview: string | null;
  error: string | null;
}

const EMPTY_SLOT: PhotoSlot = { file: null, preview: null, error: null };

// ─── Photo Slot Component ─────────────────────────────────────────────────────
function PhotoSlotCard({
  slot,
  index,
  dragOver,
  onFile,
  onRemove,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  slot: PhotoSlot;
  index: number;
  dragOver: boolean;
  onFile: (idx: number, file: File) => void;
  onRemove: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number, file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) onDrop(index, file);
    onDragLeave();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(index, file);
    e.target.value = '';
  };

  return (
    <div className="relative group">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
      <motion.div
        onClick={() => !slot.file && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
        whileHover={!slot.file ? { scale: 1.02 } : {}}
        className={`
          relative w-full aspect-square rounded-2xl border-2 transition-all overflow-hidden
          ${slot.file
            ? 'border-cyan-500/60 cursor-default'
            : dragOver
              ? 'border-cyan-400 bg-cyan-950/40 cursor-pointer'
              : slot.error
                ? 'border-red-500/70 bg-red-950/20 cursor-pointer'
                : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06] cursor-pointer'
          }
        `}
      >
        {slot.file && slot.preview ? (
          // Preview
          <>
            <img
              src={slot.preview}
              alt={`Zdjęcie ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white/80 text-sm font-medium" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Zmień zdjęcie
              </span>
            </div>
            {/* Remove button */}
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(index); }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:bg-red-900/80 transition-all opacity-0 group-hover:opacity-100"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {/* Size badge */}
            <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 text-xs text-white/60" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {(slot.file.size / 1024 / 1024).toFixed(1)} MB
            </div>
          </>
        ) : slot.error ? (
          // Error state
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 shrink-0" />
            <p className="text-xs text-red-300 leading-tight" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              {slot.error}
            </p>
            <p className="text-xs text-white/40" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Kliknij by wybrać ponownie</p>
          </div>
        ) : (
          // Empty state
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            {dragOver ? (
              <>
                <Upload className="w-9 h-9 text-cyan-400" />
                <p className="text-sm font-bold text-cyan-300" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Upuść zdjęcie</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <ImagePlus className="w-6 h-6 text-white/30" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white/50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    Zdjęcie {index + 1}
                  </p>
                  <p className="text-xs text-white/25 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    kliknij lub przeciągnij
                  </p>
                  <p className="text-xs text-white/20 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    max 5 MB
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* Click to replace — only when has file */}
      {slot.file && (
        <button
          onClick={() => inputRef.current?.click()}
          className="mt-1.5 w-full text-xs text-white/30 hover:text-white/60 text-center transition-colors"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Zmień
        </button>
      )}
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen({ onBack }: { onBack: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center text-center max-w-lg mx-auto py-12 px-4"
    >
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
        className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
        style={{
          background: 'linear-gradient(135deg, oklch(0.4 0.15 195), oklch(0.3 0.12 195))',
          boxShadow: '0 0 40px oklch(0.7 0.3 195 / 0.25)',
        }}
      >
        <CheckCircle2 className="w-10 h-10 text-cyan-300" />
      </motion.div>

      <h2
        className="text-2xl sm:text-3xl font-black text-white mb-3"
        style={{ fontFamily: 'Orbitron, sans-serif' }}
      >
        ZGŁOSZENIE WYSŁANE!
      </h2>

      <p
        className="text-muted-foreground text-base leading-relaxed mb-2"
        style={{ fontFamily: 'Rajdhani, sans-serif' }}
      >
        Twoje zgłoszenie zostało przyjęte. Skontaktujemy się z Tobą w ciągu
      </p>

      {/* Highlight */}
      <div
        className="px-6 py-3 rounded-xl border border-cyan-500/30 bg-cyan-950/30 mb-8"
        style={{ boxShadow: '0 0 20px oklch(0.7 0.3 195 / 0.12)' }}
      >
        <p
          className="text-xl font-black text-cyan-300"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          3 DNI ROBOCZYCH
        </p>
      </div>

      <p
        className="text-sm text-muted-foreground mb-8 max-w-xs"
        style={{ fontFamily: 'Rajdhani, sans-serif' }}
      >
        Otrzymasz wiadomość e-mail z potwierdzeniem oraz wyceną Twojego projektu.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
        <Link
          to="/"
          className="flex-1 px-5 py-3 rounded-xl text-sm font-bold text-center bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/70 hover:text-white"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          Strona główna
        </Link>
        <button
          onClick={onBack}
          className="flex-1 px-5 py-3 rounded-xl text-sm font-bold text-white transition-all"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            background: 'linear-gradient(135deg, oklch(0.5 0.2 195), oklch(0.4 0.18 195))',
            boxShadow: '0 0 20px oklch(0.7 0.3 195 / 0.2)',
          }}
        >
          Nowe zgłoszenie
        </button>
      </div>
    </motion.div>
  );
}

// ─── Not Logged In Screen ─────────────────────────────────────────────────────
function LoginRequired() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center text-center max-w-md mx-auto py-12 px-4"
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: 'oklch(0.18 0.04 280)', border: '1px solid oklch(0.28 0.07 280)' }}
      >
        <Sparkles className="w-8 h-8 text-primary" />
      </div>
      <h2
        className="text-xl sm:text-2xl font-black text-white mb-3"
        style={{ fontFamily: 'Orbitron, sans-serif' }}
      >
        WYMAGANE KONTO
      </h2>
      <p
        className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs"
        style={{ fontFamily: 'Rajdhani, sans-serif' }}
      >
        Z personalizacji mogą korzystać tylko użytkownicy posiadający konto w SOKAO-TOYS.
        Zaloguj się lub zarejestruj, by złożyć zamówienie.
      </p>
      <Link
        to="/"
        className="px-7 py-3 rounded-xl font-black text-sm tracking-widest text-white transition-all"
        style={{
          fontFamily: 'Orbitron, sans-serif',
          background: 'linear-gradient(135deg, var(--primary), var(--accent))',
          boxShadow: '0 0 24px oklch(0.65 0.32 280 / 0.35)',
        }}
      >
        ZALOGUJ SIĘ
      </Link>
      <p className="text-xs text-muted-foreground mt-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        Zostaniesz przekierowany na stronę główną
      </p>
    </motion.div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PersonalizacjaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [slots, setSlots] = useState<PhotoSlot[]>([{ ...EMPTY_SLOT }, { ...EMPTY_SLOT }, { ...EMPTY_SLOT }]);
  const [description, setDescription] = useState('');
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ── Photo validation & add ─────────────────────────────────────────────────
  const processFile = useCallback((idx: number, file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setSlots(prev => {
        const next = [...prev];
        next[idx] = { file: null, preview: null, error: 'Nieobsługiwany format. Użyj JPG, PNG lub WebP.' };
        return next;
      });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      setSlots(prev => {
        const next = [...prev];
        next[idx] = { file: null, preview: null, error: `Za duży plik (${sizeMB} MB). Limit to 5 MB.` };
        return next;
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setSlots(prev => {
        const next = [...prev];
        next[idx] = { file, preview: e.target?.result as string, error: null };
        return next;
      });
    };
    reader.readAsDataURL(file);
  }, []);

  const removePhoto = useCallback((idx: number) => {
    setSlots(prev => {
      const next = [...prev];
      next[idx] = { ...EMPTY_SLOT };
      return next;
    });
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!user) return;

    const trimmedDesc = description.trim();
    if (trimmedDesc.length < MIN_DESC) {
      toast.error(`Opis jest za krótki — minimum ${MIN_DESC} znaków`);
      return;
    }

    const validSlots = slots.filter(s => s.file !== null);
    const hasError = slots.some(s => s.error);
    if (hasError) {
      toast.error('Usuń błędne pliki przed wysłaniem');
      return;
    }

    setUploading(true);
    try {
      const photoUrls: string[] = [];

      for (const slot of validSlots) {
        if (!slot.file) continue;
        const ext = slot.file.name.split('.').pop() ?? 'jpg';
        const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

        const { error: uploadError } = await (supabase as any).storage
          .from('custom-order-photos')
          .upload(fileName, slot.file, { contentType: slot.file.type, upsert: false });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = (supabase as any).storage
          .from('custom-order-photos')
          .getPublicUrl(fileName);

        photoUrls.push(publicUrl);
      }

      const { error: dbError } = await (supabase as any)
        .from('custom_orders')
        .insert({ user_id: user.id, description: trimmedDesc, photo_urls: photoUrls });

      if (dbError) throw dbError;

      setSubmitted(true);
    } catch (err) {
      console.error('Błąd zapisu:', err);
      toast.error('Nie udało się wysłać zgłoszenia. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSlots([{ ...EMPTY_SLOT }, { ...EMPTY_SLOT }, { ...EMPTY_SLOT }]);
    setDescription('');
    setSubmitted(false);
  };

  const validPhotos = slots.filter(s => s.file !== null).length;
  const descLen = description.trim().length;
  const canSubmit = descLen >= MIN_DESC && !uploading && !slots.some(s => s.error);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Top bar ───────────────────────────────────────────────────── */}
      <div
        className="sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 py-3 border-b"
        style={{
          background: 'oklch(0.10 0.025 280 / 0.97)',
          borderColor: 'oklch(0.22 0.04 280)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Wróć</span>
        </button>
        <div className="flex-1" />
        <span
          className="text-xs font-bold tracking-widest text-muted-foreground"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          PERSONALIZOWANE
        </span>
        <div className="flex-1" />
        {/* Spacer to center the label */}
        <div className="w-10" />
      </div>

      {/* ── Content ───────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <AnimatePresence mode="wait">
          {!user ? (
            <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <LoginRequired />
            </motion.div>
          ) : submitted ? (
            <motion.div key="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SuccessScreen onBack={resetForm} />
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {/* ── Header ─────────────────────────────────────────────── */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'oklch(0.35 0.12 195 / 0.3)', border: '1px solid oklch(0.5 0.18 195 / 0.4)' }}
                  >
                    <Sparkles className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span
                    className="text-xs font-bold tracking-widest text-cyan-400"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    PROJEKT NA ZAMÓWIENIE
                  </span>
                </div>
                <h1
                  className="text-2xl sm:text-3xl font-black text-white mb-2"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  Zamów własną figurkę
                </h1>
                <p
                  className="text-muted-foreground text-sm leading-relaxed"
                  style={{ fontFamily: 'Rajdhani, sans-serif' }}
                >
                  Wgraj do 3 zdjęć referencyjnych i opisz projekt. Skontaktujemy się z Tobą w ciągu{' '}
                  <span className="text-cyan-400 font-semibold">3 dni roboczych</span> z wyceną.
                </p>
              </div>

              {/* ── Photo Upload ────────────────────────────────────────── */}
              <section className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <label
                    className="text-sm font-bold text-white/80"
                    style={{ fontFamily: 'Rajdhani, sans-serif' }}
                  >
                    Zdjęcia referencyjne
                    <span className="text-white/30 font-normal ml-1">(opcjonalne, max 3)</span>
                  </label>
                  <span
                    className="text-xs text-muted-foreground"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {validPhotos}/3
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {slots.map((slot, idx) => (
                    <PhotoSlotCard
                      key={idx}
                      slot={slot}
                      index={idx}
                      dragOver={dragOver === idx}
                      onFile={processFile}
                      onRemove={removePhoto}
                      onDragOver={setDragOver}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={processFile}
                    />
                  ))}
                </div>

                <p
                  className="text-xs text-muted-foreground mt-2"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  JPG / PNG / WebP · max 5 MB na zdjęcie · Możesz przeciągnąć pliki z pulpitu
                </p>
              </section>

              {/* ── Description ─────────────────────────────────────────── */}
              <section className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <label
                    className="text-sm font-bold text-white/80"
                    htmlFor="description"
                    style={{ fontFamily: 'Rajdhani, sans-serif' }}
                  >
                    Opis projektu
                    <span className="text-red-400 ml-1">*</span>
                  </label>
                  <span
                    className={`text-xs transition-colors ${descLen < MIN_DESC ? 'text-muted-foreground' : 'text-cyan-400'}`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {descLen} / {MAX_DESC}
                  </span>
                </div>

                <textarea
                  id="description"
                  value={description}
                  onChange={e => setDescription(e.target.value.slice(0, MAX_DESC))}
                  placeholder="Opisz swojego projektu: postać, wymiary, kolor, materiał, szczegóły... Im więcej informacji, tym lepsza wycena. Możesz też podać link do inspiracji lub opisać okazję (urodziny, prezent, kolekcja)."
                  rows={6}
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder:text-muted-foreground/50 resize-none outline-none transition-all focus:ring-2"
                  style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    background: 'oklch(0.14 0.028 280)',
                    border: `1px solid ${descLen > 0 && descLen < MIN_DESC ? 'oklch(0.5 0.2 30 / 0.6)' : descLen >= MIN_DESC ? 'oklch(0.5 0.18 195 / 0.5)' : 'oklch(0.25 0.04 280)'}`,
                    lineHeight: '1.6',
                  }}
                />

                {descLen > 0 && descLen < MIN_DESC && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-orange-400 mt-1.5"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    Jeszcze {MIN_DESC - descLen} znaków…
                  </motion.p>
                )}
              </section>

              {/* ── Submit ──────────────────────────────────────────────── */}
              <div
                className="rounded-2xl border p-5 flex flex-col sm:flex-row items-center gap-4"
                style={{
                  background: 'oklch(0.13 0.03 280)',
                  borderColor: 'oklch(0.22 0.04 280)',
                }}
              >
                <div className="flex-1 text-center sm:text-left">
                  <p
                    className="text-sm font-bold text-white/70"
                    style={{ fontFamily: 'Rajdhani, sans-serif' }}
                  >
                    {validPhotos > 0
                      ? `${validPhotos} zdjęcie${validPhotos > 1 ? (validPhotos === 2 ? 'a' : '') : ''} gotowe`
                      : 'Bez zdjęć'
                    }
                    {' · '}
                    {descLen >= MIN_DESC
                      ? <span className="text-cyan-400">Opis gotowy ✓</span>
                      : <span className="text-orange-400/80">Opis wymagany</span>
                    }
                  </p>
                  <p
                    className="text-xs text-muted-foreground mt-0.5"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    Odpowiedź w ciągu 3 dni roboczych
                  </p>
                </div>

                <motion.button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  whileHover={canSubmit ? { scale: 1.03 } : {}}
                  whileTap={canSubmit ? { scale: 0.97 } : {}}
                  className="shrink-0 flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: 'Orbitron, sans-serif',
                    background: canSubmit
                      ? 'linear-gradient(135deg, oklch(0.55 0.22 195), oklch(0.42 0.18 195))'
                      : 'oklch(0.22 0.04 280)',
                    color: canSubmit ? '#fff' : 'oklch(0.5 0.04 280)',
                    boxShadow: canSubmit ? '0 0 24px oklch(0.7 0.3 195 / 0.25)' : 'none',
                  }}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Wysyłanie…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>WYŚLIJ ZGŁOSZENIE</span>
                    </>
                  )}
                </motion.button>
              </div>

              {/* ── Info box ────────────────────────────────────────────── */}
              <div
                className="mt-4 rounded-xl px-4 py-3 flex items-start gap-3"
                style={{ background: 'oklch(0.12 0.025 280)', border: '1px solid oklch(0.20 0.03 280)' }}
              >
                <AlertCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <p
                  className="text-xs text-muted-foreground leading-relaxed"
                  style={{ fontFamily: 'Rajdhani, sans-serif' }}
                >
                  Po wysłaniu zgłoszenia skontaktujemy się mailowo lub przez profil w ciągu{' '}
                  <strong className="text-white/60">3 dni roboczych</strong> z wyceną i harmonogramem realizacji.
                  Zamówienie jest możliwe tylko dla zalogowanych użytkowników konta SOKAO-TOYS.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
